import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Input,
  Row,
  Col,
  FormGroup,
  Label,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form
} from 'reactstrap';
import axios from 'axios';

const FindingsList = ({ sspId }) => {
  const [findings, setFindings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    search: ''
  });
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchFindings();
  }, [sspId, filters]);

  const fetchFindings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sspId) params.append('sspId', sspId);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(
        `${baseURL}/ssp-studio/findings?${params.toString()}`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setFindings(response.data.data);
        setStats(response.data.stats);
      } else {
        setError('Failed to load findings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleViewDetail = async (findingId) => {
    try {
      const response = await axios.get(
        `${baseURL}/ssp-studio/findings/${findingId}`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setSelectedFinding(response.data.data.finding);
        setDetailModal(true);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateFinding = async (e) => {
    e.preventDefault();

    try {
      setUpdating(true);
      const response = await axios.put(
        `${baseURL}/ssp-studio/findings/${selectedFinding._id}`,
        {
          status: selectedFinding.status,
          remediationNotes: selectedFinding.remediationNotes
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setDetailModal(false);
        fetchFindings();
      } else {
        setError('Failed to update finding');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFinding = async (findingId) => {
    if (!window.confirm('Are you sure you want to delete this finding?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${baseURL}/ssp-studio/findings/${findingId}`,
        { withCredentials: true }
      );
      if (response.data.success) {
        fetchFindings();
      } else {
        setError('Failed to delete finding');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'danger';
      case 'in-progress': return 'warning';
      case 'resolved': return 'success';
      case 'false-positive': return 'secondary';
      default: return 'secondary';
    }
  };

  if (loading && findings.length === 0) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading findings...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h4>Security Findings</h4>
          {stats && (
            <div className="d-flex gap-3">
              <span>
                <strong>Total:</strong> {stats.total}
              </span>
              <span>
                <Badge color="danger">Critical: {stats.bySeverity.critical}</Badge>
              </span>
              <span>
                <Badge color="warning">High: {stats.bySeverity.high}</Badge>
              </span>
              <span>
                <Badge color="info">Medium: {stats.bySeverity.medium}</Badge>
              </span>
              <span>
                <Badge color="secondary">Low: {stats.bySeverity.low}</Badge>
              </span>
            </div>
          )}
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>Severity</Label>
                <Input
                  type="select"
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type="select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="false-positive">False Positive</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Search</Label>
                <Input
                  type="text"
                  placeholder="Search field name, description, or reasoning..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {findings.length === 0 ? (
        <Card>
          <CardBody className="text-center">
            <i className="mdi mdi-shield-search display-4 text-muted"></i>
            <h5 className="mt-3">No Findings Found</h5>
            <p className="text-muted">Upload a scan file to generate security findings</p>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Field</th>
                  <th>Value</th>
                  <th>Controls</th>
                  <th>Status</th>
                  <th>Confidence</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {findings.map(finding => (
                  <tr key={finding._id}>
                    <td>
                      <Badge color={getSeverityColor(finding.severity)}>
                        {finding.severity?.toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <code>{finding.fieldname}</code>
                      {finding.audit_desc && (
                        <div className="text-muted small">{finding.audit_desc}</div>
                      )}
                    </td>
                    <td>
                      <span className="text-truncate" style={{ maxWidth: '200px', display: 'inline-block' }}>
                        {String(finding.value)}
                      </span>
                    </td>
                    <td>
                      {finding.nist_controls && finding.nist_controls.length > 0 ? (
                        finding.nist_controls.slice(0, 3).map((ctrl, i) => (
                          <Badge key={i} color="secondary" className="me-1">
                            {ctrl}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                      {finding.nist_controls && finding.nist_controls.length > 3 && (
                        <span className="text-muted small">
                          +{finding.nist_controls.length - 3} more
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge color={getStatusColor(finding.status)}>
                        {finding.status}
                      </Badge>
                    </td>
                    <td>{finding.confidence || 50}%</td>
                    <td>
                      <Button
                        size="sm"
                        color="link"
                        onClick={() => handleViewDetail(finding._id)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        color="link"
                        className="text-danger"
                        onClick={() => handleDeleteFinding(finding._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Finding Detail Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>
          Finding Details
        </ModalHeader>
        <ModalBody>
          {selectedFinding && (
            <Form onSubmit={handleUpdateFinding}>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label><strong>Field:</strong></Label>
                    <div><code>{selectedFinding.fieldname}</code></div>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label><strong>Value:</strong></Label>
                    <div>{String(selectedFinding.value)}</div>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label><strong>Severity:</strong></Label>
                    <div>
                      <Badge color={getSeverityColor(selectedFinding.severity)}>
                        {selectedFinding.severity?.toUpperCase()}
                      </Badge>
                    </div>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label><strong>Confidence:</strong></Label>
                    <div>{selectedFinding.confidence || 50}%</div>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Status</Label>
                    <Input
                      type="select"
                      value={selectedFinding.status}
                      onChange={(e) => setSelectedFinding({
                        ...selectedFinding,
                        status: e.target.value
                      })}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="false-positive">False Positive</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label><strong>Reasoning:</strong></Label>
                <div className="p-2 bg-light rounded">{selectedFinding.reasoning}</div>
              </FormGroup>

              <FormGroup>
                <Label><strong>Affected Controls:</strong></Label>
                <div>
                  {selectedFinding.nist_controls && selectedFinding.nist_controls.length > 0 ? (
                    selectedFinding.nist_controls.map((ctrl, i) => (
                      <Badge key={i} color="secondary" className="me-1">
                        {ctrl}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted">None</span>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Remediation Notes</Label>
                <Input
                  type="textarea"
                  rows="4"
                  value={selectedFinding.remediationNotes || ''}
                  onChange={(e) => setSelectedFinding({
                    ...selectedFinding,
                    remediationNotes: e.target.value
                  })}
                  placeholder="Enter remediation notes..."
                />
              </FormGroup>

              <div className="text-end">
                <Button color="secondary" onClick={() => setDetailModal(false)} className="me-2">
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default FindingsList;
